'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, DollarSign, Handshake } from 'lucide-react';
import Image from 'next/image';

interface ArtworkOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    id: string;
    name: string;
    price: number;
    image?: string;
  };
  onAcceptOffer: (amount: number) => void;
}

type OfferStatus = 'input' | 'counterOffer' | 'rejected' | 'accepted';

export function ArtworkOfferModal({ isOpen, onClose, product, onAcceptOffer }: ArtworkOfferModalProps) {
  const [offerAmount, setOfferAmount] = useState<string>('');
  const [status, setStatus] = useState<OfferStatus>('input');

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setOfferAmount('');
      setStatus('input');
    }
  }, [isOpen]);

  const minPrice = product.price;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(offerAmount);
    
    if (isNaN(amount) || amount <= 0) return;

    if (amount >= minPrice) {
      setStatus('accepted');
    } else if (amount >= minPrice * 0.75) {
      setStatus('counterOffer');
    } else {
      setStatus('rejected');
    }
  };

  const handleAcceptCounter = () => {
    const finalAmount = minPrice;
    setOfferAmount(finalAmount.toString());
    setStatus('accepted');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-5xl p-0 overflow-hidden border-none bg-background/80 backdrop-blur-2xl shadow-[0_0_50px_rgba(0,0,0,0.5)]">
        {/* Cinematic Background Blur */}
        {product.image && (
          <div className="absolute inset-0 -z-10 opacity-20 blur-3xl scale-110">
            <Image src={product.image} alt="" fill className="object-cover" />
          </div>
        )}

        <div className="flex flex-col md:flex-row h-full min-h-[460px]">
          {/* Left Side: Artwork Display */}
          <div className="relative w-full md:w-[58%] bg-black/20 flex items-center justify-center p-4 sm:p-8">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative w-full h-full aspect-square shadow-2xl rounded-sm overflow-hidden"
            >
              {product.image ? (
                <Image 
                  src={product.image} 
                  alt={product.name} 
                  fill 
                  className="object-contain"
                  priority 
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <Handshake className="w-12 h-12 text-muted-foreground opacity-20" />
                </div>
              )}
              {/* Subtle lighting overlay */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-black/20 via-transparent to-white/10" />
            </motion.div>
          </div>

          {/* Right Side: Negotiation UI */}
          <div className="w-full md:w-[42%] p-6 sm:p-8 flex flex-col justify-center relative">
            <div className="mb-6">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="text-3xl font-black tracking-tight mb-2 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Make an Offer
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Own this unique artwork. Your fair offer will be evaluated instantly.
                </p>
              </motion.div>
            </div>

            <div className="space-y-6">
              <div className="pb-4 border-b border-white/10">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">Artwork</p>
                <h3 className="text-xl font-bold">{product.name}</h3>
              </div>

              <AnimatePresence mode="wait">
                {status === 'input' && (
                  <motion.form 
                    key="input"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="group relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-primary" />
                      </div>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        placeholder="0.00"
                        className="flex h-11 w-full rounded-2xl border-2 border-white/5 bg-white/5 px-10 py-2 text-lg font-semibold ring-offset-background placeholder:text-muted-foreground/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-all group-hover:bg-white/10"
                        required
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 text-white hover:brightness-110 h-14 rounded-2xl font-black text-lg flex items-center justify-center gap-3 transition-all shadow-xl shadow-cyan-500/10 active:scale-[0.98]"
                    >
                      <Handshake className="h-6 w-6" />
                      Submit Offer
                    </button>
                  </motion.form>
                )}

                {status === 'rejected' && (
                  <motion.div 
                    key="rejected"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="text-center space-y-6 py-4"
                  >
                    <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                      <X className="h-8 w-8 text-destructive" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-lg">Your offer of <span className="font-bold text-destructive">${parseFloat(offerAmount).toFixed(2)}</span> is too low for our valuation of this unique piece.</p>
                      <p className="text-muted-foreground text-sm">Please reconsider increasing it.</p>
                    </div>
                    <button
                      onClick={() => setStatus('input')}
                      className="w-full bg-white/10 hover:bg-white/20 h-12 rounded-xl font-bold transition-all border border-white/10"
                    >
                      Adjust Offer
                    </button>
                  </motion.div>
                )}

                {status === 'counterOffer' && (
                  <motion.div 
                    key="counterOffer"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="space-y-6 py-2"
                  >
                    <div className="bg-orange-500/10 border border-orange-500/20 p-6 rounded-2xl text-center space-y-4">
                      <div className="mx-auto w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                        <Handshake className="h-6 w-6 text-orange-500" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground font-medium">We value your interest, but it remains a little below our minimum valuation for this unique piece.</p>
                        <p className="text-xs text-muted-foreground">Please consider a slight adjustment to finalize the acquisition.</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={() => setStatus('input')}
                        className="w-full bg-orange-500 text-white hover:brightness-110 h-14 rounded-2xl font-black text-lg transition-all shadow-lg shadow-orange-500/20"
                      >
                        Adjust Offer
                      </button>
                    </div>
                  </motion.div>
                )}

                {status === 'accepted' && (
                  <motion.div 
                    key="accepted"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="text-center space-y-6 py-4"
                  >
                    <div className="mx-auto w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center relative">
                      <motion.div 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                      >
                        <Check className="h-10 w-10 text-green-500" />
                      </motion.div>
                      <motion.div 
                        className="absolute inset-0 rounded-full border-2 border-green-500/30"
                        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xl font-bold text-green-500">Offer Accepted!</p>
                      <p className="text-5xl font-black">${parseFloat(offerAmount).toFixed(2)}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">Congratulations. This unique piece is now reserved for you!</p>
                    <button
                      onClick={() => {
                        onAcceptOffer(parseFloat(offerAmount));
                        onClose();
                      }}
                      className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 text-white hover:brightness-110 h-14 rounded-2xl font-black text-xl flex items-center justify-center gap-3 transition-all shadow-2xl shadow-cyan-500/30"
                    >
                      Add to Cart
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
